import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from "react-dom";
import styles from "./Modal.module.scss"
import classNames from 'classnames';
import _ from "lodash";
import { PopoverContext } from "../Popover";
import { Text, Heading, ButtonsGroup, FieldError } from "./../../";
import { KEYBOARD_KEYS } from '../../utils';

let lastTarget = null;
const Container = ({ target, children, closeTrigger, className, style: _style, form }) => {
    const [style, setStyle] = useState({ ..._style });
    const modalRef = useRef();

    useEffect(() => {
        const closeDialogListener = e => {
            if (e.key === KEYBOARD_KEYS.BACKSPACE && e.target.nodeName !== "INPUT" && e.target.nodeName !== "TEXTAREA") {
                e.preventDefault();
            }

            if (e.key === KEYBOARD_KEYS.ESCAPE) {
                closeTrigger && closeTrigger();
            }
        };

        document.addEventListener('keydown', closeDialogListener);

        return () => {
            document.removeEventListener('keydown', closeDialogListener);
        }
    }, [closeTrigger]);

    useLayoutEffect(() => {
        let actualTarget = null;
        if (_.isFunction(target)) {
            actualTarget = target()
        } else if (target) {
            actualTarget = target;
        } else if (lastTarget && lastTarget.isConnected) {
            actualTarget = lastTarget;
        } else {
            actualTarget = document.body;
        }
        lastTarget = null;

        if (actualTarget && modalRef.current) {
            const targetBoundingRect = actualTarget.getBoundingClientRect();
            const modalRect = modalRef.current.getBoundingClientRect();

            const x = (targetBoundingRect.left + targetBoundingRect.width / 2) - modalRect.left;
            const y = (targetBoundingRect.top + targetBoundingRect.height / 2) - (modalRect.top + modalRect.height);
            setStyle({
                opacity: 0,
                transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(0)`,
                ..._style
            });

            setTimeout(() => {
                setStyle({
                    transition: "transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)",
                    transformOrigin: "0 100%",
                    opacity: 1,
                    transform: "translate(-50%, -50%)",
                    ..._style
                })
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const Component = form ? "form" : "div";

    return <PopoverContext className={styles['modal-container']} data-modal>
        <div className={styles['mask']} onClick={closeTrigger}/>
        <Component className={classNames(styles["modal"], className)} ref={modalRef} style={style} onSubmit={form ? (e) => {
            e.preventDefault();
            return false;
        } : undefined}>
            {children}
        </Component>
    </PopoverContext>;
};

export const Content = ({ children, className, style }) => {
    if(_.isString(children)) {
        children = <Text>{children}</Text>
    }
    return <div className={classNames(styles["content"], className)} style={style}>
        {children}
    </div>
};

export const Header = ({ children }) => {
    return <header className={styles.header}>
        <Heading level={20} color="text-title">{children}</Heading>
    </header>
};

export const ModalFooter = React.memo(({ children, Content, error, warning }) => {
    let message = error || warning;
    let hasMessage = false;

    if(message) {
        hasMessage = true;
        if(_.isObject(message)) {
            if (error.moreInfo) {
                message = message.description + " - " + message.moreInfo;
            } else {
                message = message.description;
            }
        }
    }

    return <footer className={styles["footer"]}>
        <div className={styles["footer-content"]}>
            {Content && !error && <Content/>}
            {hasMessage && <FieldError message={message} type={warning ? "warning" : "error"}/>}
        </div>
        <ButtonsGroup>
            {children}
        </ButtonsGroup>
    </footer>
});

export const initModalTargetListener = _.once(() => {
    document.body.addEventListener("click", (e) => {
        lastTarget = e.target
    }, true);
});

export default ({ target, children, closeTrigger, className, style, form }) => {
    return ReactDOM.createPortal(<Container className={className} style={style} target={target}
                                            form={form} closeTrigger={closeTrigger}>{children}</Container>, document.body);
};
